import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Instagram, Star, Waves, Mountain, Users, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BookingApp from '@/components/BookingApp';

const Home = () => {
  const handleWhatsAppClick = () => {
    window.open('https://wa.me/919207641616?text=Hi!%20I%20want%20to%20book%20White%20Meadow%20Resort', '_blank');
  };

  const handleBookNowClick = () => {
    const bookingSection = document.getElementById('booking');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  return (
    <div className="min-h-screen">
      {/* Floating WhatsApp Button */}
        <a
          href="https://wa.me/919207641616"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-8 right-8 z-50 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110"
          data-testid="whatsapp-float-button"
        >
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
        </a>

        {/* Header */}
        <header className="fixed w-full z-40 bg-[#FAFAFA]/80 backdrop-blur-xl border-b border-[#3D2817]/10 transition-all duration-300">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.jpeg" alt="White Meadow" className="h-12 w-12 rounded-full object-cover" />
              <div>
                <h1 className="text-xl font-bold text-[#3D2817]">White Meadow</h1>
                <p className="text-xs text-[#2D5016]">Family Resort</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#home" className="text-[#3D2817] hover:text-[#2D5016] transition-colors">Home</a>
              <a href="#about" className="text-[#3D2817] hover:text-[#2D5016] transition-colors">About</a>
              <a href="#stay" className="text-[#3D2817] hover:text-[#2D5016] transition-colors">Stay</a>
              <a href="#experiences" className="text-[#3D2817] hover:text-[#2D5016] transition-colors">Experiences</a>
              <a href="#pricing" className="text-[#3D2817] hover:text-[#2D5016] transition-colors">Pricing</a>
              <a href="#contact" className="text-[#3D2817] hover:text-[#2D5016] transition-colors">Contact</a>
            </nav>
            <Button 
              onClick={handleBookNowClick}
              className="bg-[#2D5016] text-[#FAFAFA] hover:bg-[#223E10] px-6 py-2 rounded-none transition-all duration-300 font-medium"
              data-testid="book-now-header"
            >
              Book Now
            </Button>
          </div>
        </header>

        {/* Hero Section */}
        <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: 'url(/images/waterfall.jpg)'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/30" />
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="relative z-10 text-center text-white px-6"
          >
            <p className="text-xs tracking-[0.3em] uppercase font-semibold mb-4">Omassery, Kozhikode, Kerala</p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-medium mb-6">
              Your Private Nature Retreat
            </h1>
            <p className="text-lg sm:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
              Experience luxury amid waterfalls, tropical hills, and pristine nature
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={handleBookNowClick}
                className="bg-[#2D5016] text-[#FAFAFA] hover:bg-[#223E10] px-8 py-4 rounded-none transition-all duration-300 font-medium tracking-wide"
                data-testid="book-now-hero"
              >
                Book Now
              </Button>
              <Button 
                onClick={handleBookNowClick}
                className="border border-white text-white hover:bg-white hover:text-[#3D2817] px-8 py-4 rounded-none transition-all duration-300 font-medium tracking-wide"
                data-testid="check-availability-hero"
              >
                Check Availability
              </Button>
            </div>
          </motion.div>
        </section>

        {/* Highlights */}
        <section className="py-24 sm:py-32 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { icon: <Mountain className="w-8 h-8" />, title: 'Waterfall & Stream', desc: 'Natural beauty' },
                { icon: <Waves className="w-8 h-8" />, title: 'Swimming Pool', desc: 'Private luxury' },
                { icon: <Users className="w-8 h-8" />, title: 'Full Property', desc: 'Exclusive booking' },
                { icon: <Sparkles className="w-8 h-8" />, title: 'Event Friendly', desc: 'Celebrations welcome' },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="text-center"
                >
                  <div className="flex justify-center mb-4 text-[#2D5016]">{item.icon}</div>
                  <h3 className="text-xl font-medium text-[#3D2817] mb-2">{item.title}</h3>
                  <p className="text-[#5C4633]">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-24 sm:py-32 bg-[#F5E6D3]">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="lg:col-span-7"
              >
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#2D5016] mb-4">About Us</p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#3D2817] mb-6">
                  A Sanctuary of Peace & Privacy
                </h2>
                <p className="text-base sm:text-lg leading-relaxed text-[#5C4633] mb-6">
                  Nestled in the lush hills of Omassery, White Meadow Family Resort offers a rare escape from the everyday. Our private property is designed for families, friends, and couples seeking an exclusive retreat surrounded by nature's finest elements.
                </p>
                <p className="text-base sm:text-lg leading-relaxed text-[#5C4633] mb-6">
                  With a pristine waterfall, flowing stream, luxury swimming pool, and thoughtfully designed accommodations, we provide the perfect setting for celebrations, corporate retreats, or simply unwinding in tranquility.
                </p>
                <p className="text-base sm:text-lg leading-relaxed text-[#5C4633]">
                  <strong>Our Vision:</strong> To create unforgettable experiences where luxury meets nature, privacy meets community, and every moment becomes a cherished memory.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="lg:col-span-5"
              >
                <img 
                  src="/images/about-resort.jpg" 
                  alt="White Meadow Resort" 
                  className="w-full h-[500px] object-cover"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stay & Accommodation */}
        <section id="stay" className="py-24 sm:py-32 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#2D5016] mb-4">Accommodations</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#3D2817]">Stay in Style</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12">
              {/* Hobbit Houses - larger card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="md:col-span-8 bg-[#F5E6D3] border border-[#3D2817]/10 overflow-hidden group"
              >
                <div className="overflow-hidden h-[400px]">
                  <img 
                    src="/images/hobbit-houses.jpg" 
                    alt="Hobbit Houses" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl sm:text-3xl font-medium text-[#3D2817] mb-3">Hobbit Houses (2)</h3>
                  <p className="text-base leading-relaxed text-[#5C4633] mb-4">
                    Unique, whimsical accommodations inspired by nature. Perfect for couples or small families seeking a magical stay.
                  </p>
                  <ul className="text-sm text-[#5C4633] space-y-1">
                    <li>✓ Cozy interiors with modern amenities</li>
                    <li>✓ Private garden views</li>
                    <li>✓ Capacity: 2-4 guests each</li>
                  </ul>
                </div>
              </motion.div>

              {/* Cottages - smaller card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="md:col-span-4 bg-[#F5E6D3] border border-[#3D2817]/10 overflow-hidden group"
              >
                <div className="overflow-hidden h-[250px]">
                  <img 
                    src="/images/cottages.jpg" 
                    alt="Cottages" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-xl sm:text-2xl font-medium text-[#3D2817] mb-3">Cottages (3)</h3>
                  <p className="text-sm leading-relaxed text-[#5C4633] mb-4">
                    Elegant wooden cottages with luxury interiors and nature views.
                  </p>
                  <ul className="text-xs text-[#5C4633] space-y-1">
                    <li>✓ Premium bedding</li>
                    <li>✓ AC & heating</li>
                    <li>✓ 4-6 guests</li>
                  </ul>
                </div>
              </motion.div>

              {/* Dormitory */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="md:col-span-12 bg-[#F5E6D3] border border-[#3D2817]/10 overflow-hidden group"
              >
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="overflow-hidden h-[350px]">
                    <img 
                      src="/images/dormitory.jpg" 
                      alt="Dormitory" 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <h3 className="text-2xl sm:text-3xl font-medium text-[#3D2817] mb-3">Dormitory (1)</h3>
                    <p className="text-base leading-relaxed text-[#5C4633] mb-4">
                      Spacious dormitory-style accommodation ideal for large groups and events. Sleep up to 16 guests comfortably.
                    </p>
                    <ul className="text-sm text-[#5C4633] space-y-1">
                      <li>✓ Bunk bed arrangements</li>
                      <li>✓ Shared bathrooms & common area</li>
                      <li>✓ Perfect for group retreats & celebrations</li>
                      <li>✓ Capacity: Up to 16+ guests</li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Experiences */}
        <section id="experiences" className="py-24 sm:py-32 bg-[#F5E6D3]">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#2D5016] mb-4">Experiences</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#3D2817]">Create Memories</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  image: '/images/swimming-pool.jpg',
                  title: 'Swimming Pool',
                  desc: 'Private luxury pool surrounded by tropical greenery'
                },
                {
                  image: '/images/waterfall.jpg',
                  title: 'Natural Waterfall',
                  desc: 'Pristine waterfall and stream for nature lovers'
                },
                {
                  image: '/images/campfire.jpg',
                  title: 'Campfire Nights',
                  desc: 'Cozy campfire evenings under the stars'
                },
                {
                  image: '/images/nature-walks.jpg',
                  title: 'Nature Walks',
                  desc: 'Explore tropical hills and lush landscapes'
                },
                {
                  image: '/images/campfire.jpg',
                  title: 'Group Cooking',
                  desc: 'Outdoor kitchen for communal cooking experiences'
                },
                {
                  image: '/images/kids-play-area.jpg',
                  title: 'Kids Play Area',
                  desc: 'Safe play spaces for children to enjoy'
                },
              ].map((exp, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-[#FAFAFA] border border-[#3D2817]/10 overflow-hidden group"
                >
                  <div className="overflow-hidden h-[250px]">
                    <img 
                      src={exp.image}
                      alt={exp.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-medium text-[#3D2817] mb-2">{exp.title}</h3>
                    <p className="text-sm text-[#5C4633]">{exp.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Events */}
        <section id="events" className="py-24 sm:py-32 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#2D5016] mb-4">Events</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#3D2817] mb-4">
                Perfect for Special Occasions
              </h2>
              <p className="text-lg text-[#5C4633] max-w-2xl mx-auto">
                Host your dream celebration in our private resort setting
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="overflow-hidden group"
              >
                <img 
                  src="/images/wedding-events.jpg"
                  alt="Wedding"
                  className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="flex flex-col justify-center"
              >
                <h3 className="text-2xl sm:text-3xl font-medium text-[#3D2817] mb-6">We Host</h3>
                <ul className="space-y-4 text-base text-[#5C4633]">
                  <li className="flex items-start gap-3">
                    <span className="text-[#2D5016] text-xl">✓</span>
                    <span><strong>Small Weddings</strong> - Intimate ceremonies in a natural paradise</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#2D5016] text-xl">✓</span>
                    <span><strong>Birthday Parties</strong> - Celebrate with family and friends</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#2D5016] text-xl">✓</span>
                    <span><strong>Corporate Retreats</strong> - Team building in tranquility</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#2D5016] text-xl">✓</span>
                    <span><strong>Family Reunions</strong> - Gather loved ones in privacy</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#2D5016] text-xl">✓</span>
                    <span><strong>Custom Events</strong> - Tailored packages for your needs</span>
                  </li>
                </ul>
                <Button 
                  onClick={handleBookNowClick}
                  className="bg-[#2D5016] text-[#FAFAFA] hover:bg-[#223E10] px-8 py-4 rounded-none transition-all duration-300 font-medium tracking-wide inline-block mt-8 w-fit"
                  data-testid="inquire-events-btn"
                >
                  Enquire for Events
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-24 sm:py-32 bg-[#F5E6D3]">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#2D5016] mb-4">Pricing</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#3D2817] mb-4">
                Transparent Pricing
              </h2>
              <p className="text-lg text-[#5C4633] max-w-2xl mx-auto">
                Full property booking for your exclusive use
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="bg-[#FAFAFA] border border-[#3D2817]/10 p-8 text-center"
              >
                <h3 className="text-2xl font-medium text-[#3D2817] mb-4">Cottage</h3>
                <div className="mb-6">
                  <p className="text-sm text-[#5C4633] mb-2">Weekday</p>
                  <p className="text-4xl font-bold text-[#2D5016]">₹3,999</p>
                  <p className="text-sm text-[#5C4633] mt-2">per night</p>
                </div>
                <div>
                  <p className="text-sm text-[#5C4633] mb-2">Weekend</p>
                  <p className="text-4xl font-bold text-[#2D5016]">₹4,999</p>
                  <p className="text-sm text-[#5C4633] mt-2">per night</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-[#FAFAFA] border border-[#3D2817]/10 p-8 text-center"
              >
                <h3 className="text-2xl font-medium text-[#3D2817] mb-4">Dormitory</h3>
                <div className="mb-6">
                  <p className="text-sm text-[#5C4633] mb-2">Weekday</p>
                  <p className="text-4xl font-bold text-[#2D5016]">₹17,999</p>
                  <p className="text-sm text-[#5C4633] mt-2">per night</p>
                </div>
                <div>
                  <p className="text-sm text-[#5C4633] mb-2">Weekend</p>
                  <p className="text-4xl font-bold text-[#2D5016]">₹19,999</p>
                  <p className="text-sm text-[#5C4633] mt-2">per night</p>
                </div>
                <p className="text-xs text-[#5C4633] mt-4">+ ₹1,200 per person above 16 guests</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-[#FAFAFA] border border-[#3D2817]/10 p-8 text-center"
              >
                <h3 className="text-2xl font-medium text-[#3D2817] mb-4">Full Property</h3>
                <div className="mb-6">
                  <p className="text-sm text-[#5C4633] mb-2">Weekday</p>
                  <p className="text-4xl font-bold text-[#2D5016]">₹27,999</p>
                  <p className="text-sm text-[#5C4633] mt-2">per night</p>
                </div>
                <div>
                  <p className="text-sm text-[#5C4633] mb-2">Weekend</p>
                  <p className="text-4xl font-bold text-[#2D5016]">₹29,999</p>
                  <p className="text-sm text-[#5C4633] mt-2">per night</p>
                </div>
                <p className="text-xs text-[#5C4633] mt-4">+ ₹1,200 per person above 25 guests</p>
              </motion.div>
            </div>

            <p className="text-center text-sm text-[#5C4633] mt-12">
              * Weekend = Friday & Saturday | Prices subject to seasonal variations
            </p>
          </div>
        </section>

        {/* Booking System */}
        <section id="booking" className="py-24 sm:py-32 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="text-center mb-12">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#2D5016] mb-4">Book Your Stay</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#3D2817]">Reserve Now</h2>
            </div>
            <BookingApp />
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 sm:py-32 bg-[#F5E6D3]">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#2D5016] mb-4">Reviews</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#3D2817] mb-4">What Our Guests Say</h2>
              <a 
                href="https://www.google.com/maps/place/White+Meadow+Family+Resort" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-[#2D5016] hover:underline"
              >
                Read more reviews on Google →
              </a>
            </div>

            {/* Google Reviews Embed */}
            <div className="mb-12 max-w-4xl mx-auto">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3908.0942857486835!2d75.96270897502634!3d11.385168088945087!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTHCsDIzJzA2LjYiTiA3NcKwNTcnNTQuMyJF!5e0!3m2!1sen!2sin!4v1709234567890!5m2!1sen!2sin"
                width="100%" 
                height="300" 
                style={{ border: 0 }}
                allowFullScreen="" 
                loading="lazy"
                title="Google Reviews"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Ananya & Rahul',
                  text: 'The perfect escape! The waterfall and pool made our anniversary unforgettable. Privacy at its finest.',
                  rating: 5
                },
                {
                  name: 'Corporate Team, Bangalore',
                  text: 'Excellent venue for our team retreat. The property is well-maintained and staff is very cooperative.',
                  rating: 5
                },
                {
                  name: 'Priya Family',
                  text: 'Our kids loved the play area and pool. Adults enjoyed the peaceful surroundings. Highly recommend!',
                  rating: 5
                },
              ].map((testimonial, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-[#FAFAFA] border border-[#3D2817]/10 p-8"
                >
                  <div className="flex gap-1 mb-4 text-[#FFD700]">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" />
                    ))}
                  </div>
                  <p className="text-base text-[#5C4633] mb-6 italic">"{testimonial.text}"</p>
                  <p className="text-sm font-semibold text-[#3D2817]">{testimonial.name}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-24 sm:py-32 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#2D5016] mb-4">Contact</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#3D2817]">Get in Touch</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
              >
                <h3 className="text-2xl font-medium text-[#3D2817] mb-6">Visit Us</h3>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#2D5016] flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-base text-[#5C4633]">White Meadow Family Resort</p>
                      <p className="text-sm text-[#5C4633]">Omassery, Kozhikode, Kerala, India</p>
                      <a 
                        href="https://maps.app.goo.gl/br4VXzgJG6M5Gn6b6" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-[#2D5016] hover:underline"
                      >
                        View on Google Maps →
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-[#2D5016]" />
                    <div>
                      <a href="tel:+919207641616" className="text-base text-[#5C4633] hover:text-[#2D5016]">
                        +91 920 764 1616
                      </a>
                      <br />
                      <a href="tel:+919582399203" className="text-base text-[#5C4633] hover:text-[#2D5016]">
                        +91 958 239 9203
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Instagram className="w-5 h-5 text-[#2D5016]" />
                    <a 
                      href="https://www.instagram.com/white_meado_resort" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-base text-[#5C4633] hover:text-[#2D5016]"
                    >
                      @white.meadow.family.resort
                    </a>
                  </div>
                </div>

                <Button 
                  onClick={handleWhatsAppClick}
                  className="bg-[#25D366] text-white hover:bg-[#20BA5A] px-8 py-4 rounded-none transition-all duration-300 font-medium tracking-wide inline-flex items-center gap-2"
                  data-testid="whatsapp-contact-btn"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Chat on WhatsApp
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="h-[500px] overflow-hidden"
              >
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3908.0942857486835!2d75.96270897502634!3d11.385168088945087!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTHCsDIzJzA2LjYiTiA3NcKwNTcnNTQuMyJF!5e0!3m2!1sen!2sin!4v1709234567890!5m2!1sen!2sin" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0, filter: 'grayscale(20%)' }}
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="White Meadow Location"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#3D2817] text-white py-12">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-medium mb-4">White Meadow</h3>
                <p className="text-sm text-white/70">
                  Your private nature retreat in Kozhikode, Kerala
                </p>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-4">Quick Links</h3>
                <div className="space-y-2 text-sm">
                  <a href="#about" className="block text-white/70 hover:text-white">About</a>
                  <a href="#stay" className="block text-white/70 hover:text-white">Accommodations</a>
                  <a href="#experiences" className="block text-white/70 hover:text-white">Experiences</a>
                  <a href="#pricing" className="block text-white/70 hover:text-white">Pricing</a>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-4">Connect</h3>
                <div className="space-y-2 text-sm">
                  <a href="tel:+919207641616" className="block text-white/70 hover:text-white">+91 920 764 1616</a>
                  <a href="https://www.instagram.com/white_meado_resort" target="_blank" rel="noopener noreferrer" className="block text-white/70 hover:text-white">Instagram</a>
                  <a href="https://wa.me/919207641616" target="_blank" rel="noopener noreferrer" className="block text-white/70 hover:text-white">WhatsApp</a>
                </div>
              </div>
            </div>
            <div className="border-t border-white/20 pt-8 text-center text-sm text-white/60">
              <p>© 2026 White Meadow Family Resort. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
  );
};

export default Home;